import React, { useEffect, Component, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate, Link } from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { loginRequest } from "./authConfig";
import { AuthProvider, useAuth } from "./AuthContext";
import { ThemeProvider } from './shared/ThemeContext';
import './App.css';
import LandingPage from './shared/components/LandingPage';
import RequireAuth from './shared/components/RequireAuth';
import { getInternalApps } from './shared/appRegistry';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#888' }}>An unexpected error occurred. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const MicrosoftIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" fill="#f15126" />
    <rect x="13" y="1" width="10" height="10" fill="#7cbb00" />
    <rect x="1" y="13" width="10" height="10" fill="#00a4ef" />
    <rect x="13" y="13" width="10" height="10" fill="#ffb900" />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" fill="currentColor"/>
  </svg>
);

function LoginPage() {
  const { loginWithGoogle, loginWithGitHub, providerConfig, isAuthenticated } = useAuth();
  const { instance } = useMsal();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle GitHub OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code && state === 'github-oauth') {
      setLoading(true);
      setError(null);
      loginWithGitHub(code)
        .then(() => navigate('/', { replace: true }))
        .catch(err => {
          setError(err.message || 'GitHub login failed');
          setLoading(false);
        });
    }
  }, [location.search, loginWithGitHub, navigate]);

  // If already authenticated, go home
  if (isAuthenticated) {
    const from = location.state?.from || '/';
    return <Navigate to={from} replace />;
  }

  const handleMicrosoftLogin = () => {
    setError(null);
    instance.loginRedirect(loginRequest).catch(e => {
      console.error("Microsoft login failed:", e);
      setError("Microsoft login failed. Please try again.");
    });
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError(null);
    try {
      await loginWithGoogle(credentialResponse);
      navigate(location.state?.from || '/', { replace: true });
    } catch (err) {
      setError(err.message || 'Google login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGitHubLogin = () => {
    const clientId = providerConfig.github?.clientId;
    if (!clientId) return;
    setError(null);
    const redirectUri = encodeURIComponent(window.location.origin + '/login');
    window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=user:email&state=github-oauth`;
  };

  if (loading) {
    return (
      <div className="auth-login-container">
        <div className="auth-login-card">
          <div className="auth-login-spinner" />
          <p className="auth-login-subtitle">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-login-container">
      <div className="auth-login-card">
        <div className="auth-login-logo">🏠</div>
        <h2 className="auth-login-title">Kosmoholm</h2>
        <p className="auth-login-subtitle">Sign in to continue</p>

        {error && <p className="auth-login-error" role="alert">{error}</p>}

        <div className="auth-login-providers">
          <button className="auth-button auth-button--microsoft" onClick={handleMicrosoftLogin}>
            <MicrosoftIcon />
            Continue with Microsoft
          </button>

          {providerConfig.google?.configured && (
            <div className="auth-google-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google login failed')}
                theme="outline"
                size="large"
                text="continue_with"
                width="280"
              />
            </div>
          )}

          {providerConfig.github?.configured && (
            <button className="auth-button auth-button--github" onClick={handleGitHubLogin}>
              <GitHubIcon />
              Continue with GitHub
            </button>
          )}
        </div>

        <p className="auth-login-footer">
          By signing in, you agree to our terms of use.
        </p>
      </div>
    </div>
  );
}

function SignOutButton() {
  const { logout } = useAuth();
  return (
    <button className="auth-button ghost" onClick={logout}>
      Sign out
    </button>
  );
}

function AccountInfo() {
  const { user } = useAuth();
  if (!user) return null;
  return <Link to="/profile" className="account-info">{user.name}</Link>;
}

function AppContent() {
  const { isAuthenticated, user } = useAuth();
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const msalUser = instance.getActiveAccount() || accounts[0] || null;
  const appUser = user ? { name: user.name, username: user.email } : msalUser;

  return (
    <div className="App">
      <nav className="top-bar" role="navigation" aria-label="Site navigation">
        <div className="top-bar-left">
          {!isHome && location.pathname !== '/login' && (
            <button className="nav-home-button" onClick={() => navigate('/')} aria-label="Navigate to Kosmoholm home">
              ← Kosmoholm
            </button>
          )}
        </div>
        <div className="top-bar-right">
          {isAuthenticated ? (
            <>
              <AccountInfo />
              <SignOutButton />
            </>
          ) : (
            location.pathname !== '/login' && (
              <button className="auth-button primary" onClick={() => navigate('/login')}>
                Sign in
              </button>
            )
          )}
        </div>
      </nav>

      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<LandingPage user={appUser} />} />
        {getInternalApps().map(app => (
          <Route 
            key={app.id} 
            path={app.route} 
            element={<RequireAuth><app.component user={appUser} /></RequireAuth>} 
          />
        ))}
      </Routes>
    </div>
  );
}

function App({ msalInstance }) {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const content = (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <ThemeProvider>
          <ErrorBoundary>
            <Router>
              <AppContent />
            </Router>
          </ErrorBoundary>
        </ThemeProvider>
      </AuthProvider>
    </MsalProvider>
  );

  // Wrap with GoogleOAuthProvider only if configured
  if (googleClientId) {
    return (
      <GoogleOAuthProvider clientId={googleClientId}>
        {content}
      </GoogleOAuthProvider>
    );
  }

  return content;
}

export default App;
